import React, { useState } from "react";
import { api, mediaSrc } from "../api";
import { useApp } from "../context/AppContext";
import { useLongPress } from "../lib/longPress";
import { MessageActionsSheet } from "./MessageActionsSheet";
import { ForwardDialog } from "./ForwardDialog";
import { MessageInfoSheet } from "./MessageInfoSheet";
import { MessageStickerStudio } from "./MessageStickerStudio";
import { saveToDevice, mediaFilename } from "../lib/share";
import { MediaSelectBar, SelectMark } from "./MediaSelectBar";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "./ui/alert-dialog";
import { Play } from "lucide-react";
import { SenderAvatar } from "./MessageBubble";

function fmtTime(iso) {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  } catch (e) {
    return "";
  }
}

// Wrapper so each tile gets its own long-press hook instance
function TileWrap({ onLongPress, onClick, className, children, id, testid }) {
  const lp = useLongPress(onLongPress);
  return (
    <div
      id={id}
      data-testid={testid}
      onClick={onClick}
      className={className}
      style={{ WebkitTouchCallout: "none" }}
      onTouchStart={lp.onTouchStart}
      onTouchMove={lp.onTouchMove}
      onTouchEnd={lp.onTouchEnd}
      onTouchCancel={lp.onTouchCancel}
      onClickCapture={lp.onClickCapture}
      onContextMenu={lp.onContextMenu}
    >
      {children}
    </div>
  );
}

/**
 * WhatsApp-style media collage: 3+ consecutive photos/videos from the same sender
 * are grouped into a compact grid for a cleaner conversation (e.g. photo bursts,
 * video markup sequences). Tap a tile to open the photo/video viewer.
 * Long-press (or right-click) a tile for actions: save / forward / delete / reply.
 */
export function MediaGroupBubble({ messages, sender, own, conversation, onOpenPhoto, onOpenVideo, focusedId, onReply, onReact, starredIds, onToggleStar, onPin }) {
  const { user, t } = useApp();
  const [actionItem, setActionItem] = useState(null); // {media, message}
  const [picking, setPicking] = useState(false); // "Select" mode: pick tiles → save/share many
  const [picked, setPicked] = useState([]); // media ids
  const togglePick = (it) => setPicked((p) => (p.includes(it.media.id) ? p.filter((x) => x !== it.media.id) : [...p, it.media.id]));
  const [forwardMsg, setForwardMsg] = useState(null);
  const [infoMsg, setInfoMsg] = useState(null);
  const [stickerMedia, setStickerMedia] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // message

  // Flatten: one tile per media item, keeping its source message
  const items = [];
  messages.forEach((m) => {
    (m.media || []).forEach((media, idx) => {
      items.push({ media, message: m, anchor: idx === 0 });
    });
  });
  if (items.length === 0) return null;

  const MAX_TILES = 8;
  const visible = items.slice(0, MAX_TILES);
  const hiddenCount = items.length - visible.length;
  const lastMsg = messages[messages.length - 1];
  const isFocused = focusedId && messages.some((m) => m.id === focusedId);

  const openItem = (it) => {
    if (it.media.kind === "video") onOpenVideo && onOpenVideo(it.media, it.message);
    else onOpenPhoto && onOpenPhoto(it.media, it.message);
  };

  const jobOf = (it) => it.message?.meta?.linked_job || conversation?.job;
  const pickedItems = items.filter((it) => picked.includes(it.media.id)).map((it) => ({ id: it.media.id, url: mediaSrc(it.media), filename: mediaFilename(it.media, jobOf(it)), kind: it.media.kind }));
  // "Save all": every photo/video of the collage in ONE go (gallery on native / share sheet -> Photos)
  const saveAll = async () => {
    const r = await saveToDevice(items.map((it) => ({
      url: mediaSrc(it.media),
      filename: mediaFilename(it.media, jobOf(it)),
      kind: it.media.kind,
    })));
    if (r === "saved") toast.success(t("saved_to_gallery_n").replace("{n}", items.length));
    else if (r === "downloaded") toast.success(t("saved_to_device"));
    else if (r === false) toast.error(t("something_wrong"));
  };

  const deleteMsg = async (m) => {
    try {
      await api.delete(`/messages/${m.id}`);
    } catch (e) {
      toast.error(t("something_wrong"));
    }
  };

  const canDelete = actionItem ? (actionItem.message.sender_id === user.id || user.role === "admin") : false;

  return (
    <div className={`flex mb-2 ${own ? "justify-end" : "justify-start"}`} data-testid="media-group-bubble">
      {/* Profile photo next to other people's collages in GROUP chats */}
      {!own && conversation?.type !== "dm" && (
        <div className="mr-1.5 self-start mt-0.5">
          <SenderAvatar sender={sender} />
        </div>
      )}
      <div className={`max-w-[78%] sm:max-w-[65%] ${isFocused ? "ring-2 ring-accent/60 rounded-2xl" : ""}`}>
        {!own && sender && (
          <div className="text-[11px] font-semibold text-primary mb-0.5 px-1">{sender.name}</div>
        )}
        <div
          className={`grid grid-cols-2 gap-1 p-1 rounded-2xl border ${own ? "bg-primary/5 border-primary/15 rounded-br-md" : "bg-card border-border rounded-bl-md"}`}
        >
          {visible.map((it, i) => {
            const isLastWithMore = i === visible.length - 1 && hiddenCount > 0;
            return (
              <TileWrap
                key={`${it.message.id}-${it.media.id}`}
                id={it.anchor ? `msg-${it.message.id}` : undefined}
                testid="media-group-tile"
                onClick={() => (picking ? togglePick(it) : openItem(it))}
                onLongPress={() => { if (!picking) setActionItem(it); }}
                className="relative aspect-square rounded-lg overflow-hidden cursor-pointer bg-muted group/tile select-none"
              >
                {it.media.kind === "video" ? (
                  <>
                    <video
                      src={mediaSrc(it.media) + (it.media.poster ? "" : "#t=0.1")}
                      poster={it.media.poster || undefined}
                      preload="metadata"
                      muted
                      playsInline
                      className="h-full w-full object-cover pointer-events-none bg-black"
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="h-9 w-9 rounded-full bg-black/60 flex items-center justify-center">
                        <Play className="h-4.5 w-4.5 text-white ml-0.5" />
                      </div>
                    </div>
                  </>
                ) : (
                  <img
                    src={mediaSrc(it.media)}
                    alt=""
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-200 group-hover/tile:scale-[1.03]"
                  />
                )}
                {picking && <SelectMark selected={picked.includes(it.media.id)} index={picked.indexOf(it.media.id) + 1} />}
                {!picking && it.anchor && (it.message.content || "").trim() && (
                  <div
                    data-testid="media-group-caption"
                    className="absolute inset-x-0 bottom-0 px-1.5 py-1 bg-gradient-to-t from-black/70 to-transparent text-white text-[10px] leading-tight line-clamp-2 pointer-events-none"
                  >
                    {it.message.content.trim()}
                  </div>
                )}
                {isLastWithMore && !picking && (
                  <div
                    data-testid="media-group-more-overlay"
                    className="absolute inset-0 bg-black/60 flex items-center justify-center text-white font-display font-bold text-xl"
                  >
                    +{hiddenCount}
                  </div>
                )}
              </TileWrap>
            );
          })}
        </div>
        {picking && (
          <MediaSelectBar
            className="mt-1.5"
            items={pickedItems}
            onCancel={() => { setPicking(false); setPicked([]); }}
            onDelete={(own || user.role === "admin") ? async () => {
              const msgs = items.filter((it) => picked.includes(it.media.id)).map((it) => it.message);
              for (const m of msgs) await deleteMsg(m);
            } : undefined}
          />
        )}
        <div className={`flex items-center gap-1 mt-0.5 px-1 ${own ? "justify-end" : "justify-start"}`}>
          <span className="text-[10px] text-muted-foreground">
            {items.length} · {fmtTime(lastMsg.created_at)}
          </span>
        </div>
      </div>

      {/* Long-press actions for the pressed tile */}
      <MessageActionsSheet
        open={!!actionItem}
        onClose={() => setActionItem(null)}
        canCopy={false}
        canSave={!!actionItem}
        canDelete={canDelete}
        isStarred={actionItem ? !!starredIds?.has(actionItem.message.id) : false}
        onStar={onToggleStar && actionItem ? () => onToggleStar(actionItem.message) : null}
        onPin={onPin && actionItem ? () => onPin(actionItem.message) : null}
        onReact={onReact && actionItem ? (emoji) => onReact(actionItem.message, emoji) : null}
        onReply={onReply && actionItem ? () => onReply(actionItem.message) : null}
        onInfo={actionItem ? () => setInfoMsg(actionItem.message) : null}
        onMakeSticker={actionItem ? () => setStickerMedia(actionItem.media) : null}
        onCopy={() => {}}
        saveLabel={t("save_all_files").replace("{n}", items.length)}
        onSave={saveAll}
        onSelect={() => { setPicking(true); setPicked(actionItem ? [actionItem.media.id] : []); }}
        onForward={() => { setForwardMsg(actionItem?.message || null); }}
        onDelete={() => { setConfirmDelete(actionItem?.message || null); }}
      />
      {forwardMsg && (
        <ForwardDialog open message={forwardMsg} onClose={() => setForwardMsg(null)} />
      )}
      <MessageInfoSheet open={!!infoMsg} message={infoMsg} onClose={() => setInfoMsg(null)} />
      {stickerMedia && <MessageStickerStudio media={stickerMedia} onClose={() => setStickerMedia(null)} />}
      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => { if (!v) setConfirmDelete(null); }}>
        <AlertDialogContent data-testid="delete-message-confirm" className="z-[260]">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("delete_message")}</AlertDialogTitle>
            <AlertDialogDescription>{t("delete_message_confirm")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-message-cancel">{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-message-confirm-btn"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => { if (confirmDelete) deleteMsg(confirmDelete); setConfirmDelete(null); }}
            >
              {t("delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
